import React, { useEffect } from "react"
import { Card, Row, Col, Input, Alert, Form, notification } from "antd"
import { CarOutlined, FileTextOutlined, InfoCircleOutlined } from "@ant-design/icons"
import { Typography } from "antd"
import ProductInput from "pages/ProposalV3/PropDetail/ProductInput"
import {
  checkVinProductFamilyMatch,
  getReservationByVin,
  type ReservationData,
} from "services/proposal"
import { getThirdPartyAuthorizationByTpreference } from "services/thirdParty"

const { Title, Text } = Typography

type InvoiceInitialStepProps = {
  form: any
  required: boolean
  isInspect: boolean
  onVinValidation: {
    isCheckingVin: boolean
    setIsCheckingVin: (loading: boolean) => void
    setVinReserved: (reserved: boolean) => void
    setReservationData: (data: ReservationData | null) => void
    setWorkflowMode: (mode: 'initial' | 'reserved' | 'unreserved' | 'final' | 'spr') => void
    setCurrent: (step: number) => void
    setCompletedSteps: (steps: number[]) => void
    setIsLoadingAuth: (loading: boolean) => void
    setAuthBrand: (brand: string) => void
    setIsCreditNote?: (isCredit: boolean) => void
    setIsBlocked?: (blocked: boolean) => void
  }
}

const InvoiceInitialStep: React.FC<InvoiceInitialStepProps> = ({
  form,
  required,
  isInspect,
  onVinValidation
}) => {
  const familleFinancementValue = Form.useWatch("familleFinancement", form)
  const vinValue = Form.useWatch("VIN", form)

  const validateVIN = (rule: any, value: string) => {
    if (!value) return Promise.resolve()
    const vinRegex = /^[A-HJ-NPR-Z0-9]{17}$/
    if (!vinRegex.test(value)) {
      return Promise.reject("VIN doit contenir 17 caractères alphanumériques (pas de I, O, Q)")
    }
    return Promise.resolve()
  }

  const checkVinReservation = async (vin: string, productFamily: string) => {

    const {
      setIsCheckingVin,
      setVinReserved,
      setReservationData,
      setWorkflowMode,
      setCurrent,
      setCompletedSteps,
      setIsLoadingAuth,
      setAuthBrand,
      setIsCreditNote,
      setIsBlocked,
    } = onVinValidation

    try {
      setIsCheckingVin(true)

      const response = await checkVinProductFamilyMatch({
        vin: vin.trim(),
        productFamily: productFamily
      })


      if (response.code === "200" && response.data !== undefined) {
        const isReserved = response.data

        if (isReserved) {
          try {
            const reservationResponse = await getReservationByVin({ vin: vin.trim() })

            if (reservationResponse.code === "200" && reservationResponse.data) {
              const data = reservationResponse.data as any
              const reservationStatus = data.ctstatus || data.status || 'UNKNOWN'


              if (reservationStatus.toUpperCase() === 'PREFIN' || reservationStatus.toUpperCase() === 'INVOICED') {

                setVinReserved(true)
                setReservationData(reservationResponse.data)
                setWorkflowMode('final')

                if (setIsCreditNote) {
                  setIsCreditNote(true)
                }

                setCurrent(2)
                setCompletedSteps([0, 1])

                notification.success({
                  message: "Mode Avoir Automatique",
                  description: `VIN facturé (${reservationStatus}) → Avoir activé automatiquement`,
                  duration: 4,
                })
                return
              } else {
                console.log(" [VIN CHECK] Autres statuts → Mode facture normale")

                setVinReserved(true)
                setReservationData(reservationResponse.data)
                setWorkflowMode('reserved')

                // Récupération marque
                const clientRef = data.clientreference || data.clientCode
                const prcode = data.prcode || data.productFamily

                if (clientRef && prcode) {
                  try {
                    setIsLoadingAuth(true)
                    const basicResponse = await getThirdPartyAuthorizationByTpreference({
                      tpreference: clientRef,
                      prcode: prcode,
                    })

                    if (basicResponse.code === "200" && basicResponse.data?.authbrand) {
                      const detectedBrand = basicResponse.data.authbrand
                      setAuthBrand(detectedBrand)
                      form.setFieldValue("codeMarque", detectedBrand)
                      form.setFieldValue("brandLabel", detectedBrand)
                    }
                  } catch (error) {
                    console.warn("Erreur marque:", error)
                  } finally {
                    setIsLoadingAuth(false)
                  }
                }

                notification.success({
                  message: "VIN Réservé - Facture Normale",
                  description: "Mode facture normale activé automatiquement",
                  duration: 4,
                })
                return
              }
            }
          } catch (error) {
            console.error("Erreur données réservation:", error)
            notification.error({
              message: "Erreur",
              description: "Impossible de charger les données de réservation",
              duration: 5,
            })
          }
        } else {
          console.log("[VIN CHECK] VIN non réservé - vérifications supplémentaires...")
          try {
            const additionalCheck = await getReservationByVin({ vin: vin.trim() })

            if (additionalCheck.code === "200" && additionalCheck.data) {
              const reservationData = additionalCheck.data as any
              const reservationStatus = reservationData.ctstatus || reservationData.status || 'UNKNOWN'


              if (reservationStatus.toUpperCase() === 'REFUSE' || reservationStatus.toUpperCase() === 'REFUSED') {

                setVinReserved(true)
                setReservationData(reservationData)
                setWorkflowMode('initial')
                if (setIsBlocked) {
                  setIsBlocked(true)

                }
                notification.warning({
                  message: "Réservation Refusée",
                  description: "Réservation refusée - Validation manuelle possible",
                  duration: 6,
                })
                return
              }

              if (reservationStatus.toUpperCase() === 'PREFIN' || reservationStatus.toUpperCase() === 'INVOICED') {

                setVinReserved(true)
                setReservationData(reservationData)
                setWorkflowMode('reserved')

                if (setIsCreditNote) {
                  setIsCreditNote(true)
                }

                notification.success({
                  message: "Mode Avoir Automatique",
                  description: `VIN facturé (${reservationStatus}) → Avoir activé, cliquez sur "Suivant"`,
                  duration: 4,
                })
                return
              }
            }
          } catch (error) {
            console.warn("Vérification supplémentaire échouée:", error)
          }

          setWorkflowMode('unreserved')

          notification.info({
            message: "VIN Non Réservé",
            description: "Mode pré-facturation activé",
            duration: 4,
          })
        }
      } else {
        throw new Error("Erreur de vérification VIN")
      }
    } catch (error: any) {
      console.error("Erreur:", error)
      notification.error({
        message: "Erreur de vérification",
        description: error.message || "Impossible de vérifier le statut du VIN",
        duration: 5,
      })
    } finally {
      setIsCheckingVin(false)
    }
  }

  const handleProductSelection = (code: string) => {
    if (!isInspect) {
      const labels: { [key: string]: string } = {
        'VN': 'Véhicules Neufs',
        'VD': 'Véhicules Démonstration',
        'VO': 'Véhicules Occasion',
        'SPR': 'Pièces de Rechange',
        'AUTRE': 'Autre Activité'
      }

      form.setFieldsValue({
        familleFinancement: code,
        familyLabel: labels[code] || code
      })

      if (code === 'SPR') {
        const { setWorkflowMode } = onVinValidation
        setWorkflowMode('spr')

        notification.info({
          message: "Pièces de Rechange sélectionnées",
          description: "Workflow SPR activé - Pas de VIN requis. Cliquez sur \"Suivant\" pour passer à la facturation.",
          duration: 4,
        })
      }
    }
  }

  useEffect(() => {
    if (familleFinancementValue && vinValue && vinValue.length === 17 && !onVinValidation?.isCheckingVin) {
      const timeoutId = setTimeout(() => {
        checkVinReservation(vinValue, familleFinancementValue)
      }, 800)

      return () => clearTimeout(timeoutId)
    }
  }, [familleFinancementValue, vinValue])

  return (
    <Card title="Sélection du type de facture" className="wizard-step-card">
      <Row gutter={[24, 24]}>
        <Col span={24}>
          <Alert
            message="Première étape"
            description="Sélectionnez la famille de produit et saisissez le VIN pour vérifier s'il est réservé dans le système."
            type="info"
            showIcon
            style={{ marginBottom: 32, borderRadius: '8px' }}
          />
        </Col>
      </Row>

      <div className="product-family-selection">
        <Form.Item
          name="familleFinancement"
          style={{ display: 'none' }}
          rules={[{ required: true, message: "Veuillez sélectionner une famille de produit" }]}
        >
          <ProductInput
            onSelected={(value, option) => {
              form.setFieldValue("familyLabel", option?.label ?? "")
            }}
          />
        </Form.Item>

        <Title level={3}>Sélection du type de facture</Title>
        <Text type="secondary">Choisissez la famille de produit pour cette facture</Text>

        {!familleFinancementValue && (
          <Alert
            message="Sélection requise"
            description="Veuillez sélectionner une famille de produit en cliquant sur l'une des cartes ci-dessous."
            type="warning"
            showIcon
            style={{ marginTop: 16, marginBottom: 16, borderRadius: '8px' }}
          />
        )}

        <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
          <Col span={8}>
            <Card
              hoverable
              className={familleFinancementValue === 'VN' ? 'selected-card' : ''}
              onClick={() => handleProductSelection('VN')}
              style={{
                textAlign: 'center',
                cursor: 'pointer',
                border: familleFinancementValue === 'VN' ? '2px solid #1890ff' : '1px solid #d9d9d9'
              }}
            >
              <CarOutlined style={{ fontSize: 32, color: '#1890ff' }} />
              <Title level={4}>Véhicule Neuf (VN)</Title>
              <Text>Factures véhicules neufs</Text>
            </Card>
          </Col>

          <Col span={8}>
            <Card
              hoverable
              className={familleFinancementValue === 'VD' ? 'selected-card' : ''}
              onClick={() => handleProductSelection('VD')}
              style={{
                textAlign: 'center',
                cursor: 'pointer',
                border: familleFinancementValue === 'VD' ? '2px solid #1890ff' : '1px solid #d9d9d9'
              }}
            >
              <CarOutlined style={{ fontSize: 32, color: '#52c41a' }} />
              <Title level={4}>Véhicule Démonstration (VD)</Title>
              <Text>Véhicules de démonstration</Text>
            </Card>
          </Col>

          <Col span={8}>
            <Card
              hoverable
              className={familleFinancementValue === 'VO' ? 'selected-card' : ''}
              onClick={() => handleProductSelection('VO')}
              style={{
                textAlign: 'center',
                cursor: 'pointer',
                border: familleFinancementValue === 'VO' ? '2px solid #1890ff' : '1px solid #d9d9d9'
              }}
            >
              <CarOutlined style={{ fontSize: 32, color: '#faad14' }} />
              <Title level={4}>Véhicule Occasion (VO)</Title>
              <Text>Véhicules d'occasion</Text>
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col span={12}>
            <Card
              hoverable
              className={familleFinancementValue === 'SPR' ? 'selected-card' : ''}
              onClick={() => handleProductSelection('SPR')}
              style={{
                textAlign: 'center',
                cursor: 'pointer',
                border: familleFinancementValue === 'SPR' ? '2px solid #1890ff' : '1px solid #d9d9d9'
              }}
            >
              <FileTextOutlined style={{ fontSize: 32, color: '#722ed1' }} />
              <Title level={4}>Pièces de Rechange (SPR)</Title>
              <Text>Pièces et accessoires</Text>
            </Card>
          </Col>

          <Col span={12}>
            <Card
              hoverable
              className={familleFinancementValue === 'AUTRE' ? 'selected-card' : ''}
              onClick={() => handleProductSelection('AUTRE')}
              style={{
                textAlign: 'center',
                cursor: 'pointer',
                border: familleFinancementValue === 'AUTRE' ? '2px solid #1890ff' : '1px solid #d9d9d9'
              }}
            >
              <InfoCircleOutlined style={{ fontSize: 32, color: '#eb2f96' }} />
              <Title level={4}>Autre Activité</Title>
              <Text>Autres types de facturation</Text>
            </Card>
          </Col>
        </Row>

        {familleFinancementValue !== 'SPR' && (
          <Row gutter={[24, 16]} style={{ marginTop: 32 }}>
            <Col span={24}>
              <Form.Item
                name="VIN"
                label={
                  <div className="elegant-label">
                    <CarOutlined style={{ marginRight: '8px', color: '#52c41a' }} />
                    VIN (Vehicle Identification Number)
                  </div>
                }
                rules={[
                  { required: true, message: "Veuillez saisir le VIN" },
                  { validator: validateVIN }
                ]}
              >
                <div className="elegant-input-wrapper">
                  <Input
                    placeholder="VIN (17 caractères)"
                    maxLength={17}
                    disabled={isInspect}
                    size="large"
                    style={{
                      borderRadius: '8px',
                      fontFamily: 'monospace',
                      letterSpacing: '1px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                      textTransform: 'uppercase'
                    }}
                    onChange={(e) => {
                      const value = e.target.value.toUpperCase().replace(/[IOQ]/g, "")
                      form.setFieldValue("VIN", value)
                    }}
                  />
                </div>
              </Form.Item>
            </Col>
          </Row>
        )}
      </div>
    </Card>
  )
}

export default InvoiceInitialStep